import { Fragment, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { formatUsuarioLabel } from '@leadsphere/types';

import { Button } from '../../components/ui/Button.js';
import { CheckboxDropdown } from '../../components/ui/CheckboxDropdown.js';
import { ConfirmModal } from '../../components/ui/ConfirmModal.js';
import { Modal } from '../../components/ui/Modal.js';
import { ToastStack } from '../../components/ui/Toast.js';
import { useApi } from '../../hooks/useApi.js';
import { useAuth } from '../../hooks/useAuth.js';
import { useToasts } from '../../hooks/useToasts.js';
import { ApiError, descargarArchivo, uploadArchivo } from '../../lib/api.js';
import { DropzoneArchivo, fmtTamano, iconoPorMime, MAX_ADJUNTO_BYTES } from './AdjuntosUploader.js';
import { EstatusBadge, PrioridadBadge } from './badges.js';
import type { PropuestaPrioridad, Subtarea, TicketAdjunto, TicketConDetalle, TicketEstatus, TicketEvento, TicketPrioridad, TicketTipoCatalogo, UsuarioAsignable } from './types.js';

const PRIORIDAD_LABEL: Record<TicketPrioridad, string> = {
  P1: 'P1 — Urgente',
  P2: 'P2 — Normal',
  P3: 'P3 — Programable',
};

function iconoAdjunto(a: TicketAdjunto): string {
  if (a.tipo === 'url') return '🔗';
  return iconoPorMime(a.mime_type);
}

function fmtFechaHora(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function hrsLabel(h: string | null): string {
  if (h === null) return '—';
  return `${Number(h).toFixed(1)} hrs`;
}

function slaIcon(cumple: boolean | null): string {
  if (cumple === null) return '—';
  return cumple ? '✓ Sí' : '✗ No';
}

// Mismo criterio del backend (puedeReabrir en tickets.service.ts) — solo para
// no ofrecer un botón condenado a fallar; el backend es quien lo hace cumplir.
function puedeReabrirCliente(ticket: TicketConDetalle): boolean {
  if (ticket.estatus !== 'resuelta') return false;
  if (!ticket.fecha_cierre) return false;
  const horas = (Date.now() - new Date(ticket.fecha_cierre).getTime()) / 3_600_000;
  return horas <= 24;
}

// Badge "Tomado por" — el primer evento de cambio a en_progreso, el más
// antiguo (no el más reciente, que puede ser un "retomar" posterior). No
// oculta que los demás responsables también pueden actuar, es solo contexto.
function quienTomo(ticket: TicketConDetalle): string | null {
  const tomas = ticket.eventos.filter(ev => ev.tipo === 'cambio_estatus' && ev.estatus_nuevo === 'en_progreso');
  if (!tomas.length) return null;
  const primera = tomas.reduce((a, b) => (new Date(a.created_at).getTime() < new Date(b.created_at).getTime() ? a : b));
  return primera.actor_nombre;
}

// Mismo criterio que ultimoEventoRelevanteParaDevolver en tickets.service.ts:
// no se puede devolver dos veces seguidas sin un tomar/retomar (entrada a
// en_progreso) entre medio. El backend es quien lo hace cumplir (409); esto
// solo evita ofrecer un botón condenado a fallar.
function puedeDevolverCliente(ticket: TicketConDetalle): boolean {
  const relevantes = ticket.eventos.filter(ev => ev.tipo === 'devolucion' || (ev.tipo === 'cambio_estatus' && ev.estatus_nuevo === 'en_progreso'));
  if (relevantes.length === 0) return true;
  const ultimo = relevantes.reduce((a, b) => (new Date(a.created_at).getTime() > new Date(b.created_at).getTime() ? a : b));
  return ultimo.tipo !== 'devolucion';
}

// Reconstruye ícono/acción semánticos a partir de eventos reales — el backend
// siempre guarda tipo='cambio_estatus' para transiciones (SRS §9.4 literal),
// así que la distinción tomó/resuelto/cancelado/reabrió sale de estatus_nuevo.
function eventoIcono(ev: TicketEvento): string {
  if (ev.tipo === 'creacion') return '🎫';
  if (ev.tipo === 'devolucion') return '↩️';
  if (ev.tipo === 'csat') return '⭐';
  if (ev.tipo === 'cambio_estatus') {
    if (ev.estatus_nuevo === 'en_progreso') return '👤';
    if (ev.estatus_nuevo === 'resuelta') return '✅';
    if (ev.estatus_nuevo === 'cancelada') return '🚫';
    if (ev.estatus_nuevo === 'reabierta') return '🔄';
  }
  return '📝';
}

function eventoAccion(ev: TicketEvento): string {
  if (ev.tipo === 'creacion') return 'creó el ticket';
  if (ev.tipo === 'devolucion') return 'devolvió el ticket por información incompleta';
  if (ev.tipo === 'csat') return ev.descripcion ?? 'calificó la atención';
  if (ev.tipo === 'cambio_estatus') {
    if (ev.estatus_nuevo === 'en_progreso') return 'tomó / retomó el ticket';
    if (ev.estatus_nuevo === 'resuelta') return 'marcó el ticket como resuelto';
    if (ev.estatus_nuevo === 'cancelada') return 'canceló el ticket';
    if (ev.estatus_nuevo === 'reabierta') return 'reabrió el ticket';
  }
  return ev.descripcion ?? 'actualizó el ticket';
}

// Prompt AL — sub-folio visual (LS-00011-A/-B/...) calculado en frontend por
// orden de asignado_en entre las sub-tareas ACTIVAS, nunca persistido. Las
// inactivas/completadas se ordenan al final, sin letra propia (ya no cuentan
// para el orden ni para el % de progreso).
function ordenarSubtareas(folio: string, subtareas: Subtarea[]): { subtarea: Subtarea; subFolio: string | null }[] {
  const activas = subtareas
    .filter(s => s.responsable_activo)
    .sort((a, b) => new Date(a.asignado_en).getTime() - new Date(b.asignado_en).getTime());
  const inactivas = subtareas.filter(s => !s.responsable_activo);

  const activasOrdenadas = activas
    .slice()
    .sort((a, b) => {
      if (a.estado === b.estado) return new Date(a.asignado_en).getTime() - new Date(b.asignado_en).getTime();
      return a.estado === 'completada' ? 1 : -1;
    });

  const letras: { subtarea: Subtarea; subFolio: string | null }[] = activas.map((s, i) => ({
    subtarea: s,
    subFolio: `${folio}-${String.fromCharCode(65 + i)}`,
  }));
  const subFolioPorId = new Map(letras.map(l => [l.subtarea.id, l.subFolio]));

  return [
    ...activasOrdenadas.map(s => ({ subtarea: s, subFolio: subFolioPorId.get(s.id) ?? null })),
    ...inactivas.map(s => ({ subtarea: s, subFolio: null })),
  ];
}

const CSAT_LABEL = ['', 'Muy insatisfecho', 'Insatisfecho', 'Neutral', 'Satisfecho', 'Muy satisfecho'];

const ESTATUS_CONFIRM: Record<TicketEstatus, { titulo: string; body: string; danger?: boolean; confirmLabel: string }> = {
  en_progreso: { titulo: '¿Cambiar estatus a "En progreso"?', body: 'Se actualizará el estatus del ticket.', confirmLabel: 'Confirmar' },
  resuelta:    { titulo: '¿Cambiar estatus a "Resuelta"?', body: 'El ticket quedará marcado como resuelto. El solicitante podrá calificar la atención.', confirmLabel: 'Confirmar' },
  reabierta:   { titulo: '¿Cambiar estatus a "Reabierta"?', body: 'Se actualizará el estatus del ticket.', confirmLabel: 'Confirmar' },
  cancelada:   { titulo: '¿Cambiar estatus a "Cancelada"?', body: 'El ticket será cancelado y no podrá reabrirse.', danger: true, confirmLabel: 'Cancelar ticket' },
  nueva:       { titulo: '¿Cambiar estatus?', body: 'Se actualizará el estatus del ticket.', confirmLabel: 'Confirmar' },
};

export function TicketDetalle() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { get, patch, post, delete: del } = useApi();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { toasts, showToast, dismiss } = useToasts();

  const [estatusPendiente, setEstatusPendiente] = useState<TicketEstatus | null>(null);
  const [devolverAbierto, setDevolverAbierto] = useState(false);
  const [devolverComentario, setDevolverComentario] = useState('');
  const [csatValor, setCsatValor] = useState(0);
  const [csatHover, setCsatHover] = useState(0);
  const [csatComentario, setCsatComentario] = useState('');
  const [subiendoArchivo, setSubiendoArchivo] = useState(false);
  const [progresoSubida, setProgresoSubida] = useState(0);
  const [linkAbierto, setLinkAbierto] = useState(false);
  const [linkNombre, setLinkNombre] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [responsablesAbierto, setResponsablesAbierto] = useState(false);
  const [responsablesSeleccion, setResponsablesSeleccion] = useState<string[]>([]);

  // Prompt AL (Sub-tareas)
  const [subtareaConfirmPendiente, setSubtareaConfirmPendiente] = useState<{ id: string; nuevoEstado: 'pendiente' | 'completada' } | null>(null);
  const [addSubtareaAbierto, setAddSubtareaAbierto] = useState(false);
  const [addResponsableId, setAddResponsableId] = useState('');
  const [addTipoId, setAddTipoId] = useState('');
  const [addDetalle, setAddDetalle] = useState('');

  // Prompt AN (Cambio de SLA)
  const [proponerAbierto, setProponerAbierto] = useState(false);
  const [nuevaPrioridad, setNuevaPrioridad] = useState<TicketPrioridad | ''>('');
  const [motivoPropuesta, setMotivoPropuesta] = useState('');
  const [decisionPendiente, setDecisionPendiente] = useState<'aprobado' | 'rechazado' | null>(null);

  const { data: ticket, isLoading } = useQuery({
    queryKey: ['tickets', 'detalle', id],
    queryFn: () => get<TicketConDetalle>(`/tickets/${id}`),
    enabled: Boolean(id),
  });

  // Solo se carga cuando algún modal que lo necesita está abierto — solo lo
  // abre quien puede gestionar el ticket (ver puedeGestionarTicket).
  // Prompt BJ-11 — /usuarios/asignables en vez de /usuarios: este dropdown es
  // para CUALQUIER rol autenticado (asignar responsable de ticket), GET
  // /usuarios está restringido a root/head/analista/kam/CEO (gestión de
  // usuarios) y causaba 403 a digital/comercial/marketing.
  const { data: usuariosData } = useQuery({
    queryKey: ['usuarios-asignables'],
    queryFn: () => get<UsuarioAsignable[]>('/usuarios/asignables'),
    enabled: responsablesAbierto || addSubtareaAbierto,
  });
  const usuariosActivos = useMemo(() => (usuariosData ?? []).filter(u => u.activo), [usuariosData]);

  // Prompt AL — candidatos para "+ Añadir Subtarea": cualquier usuario activo
  // que no tenga YA una sub-tarea activa en este ticket (re-agregar a alguien
  // inactivo reactiva su fila, ver agregarSubtarea() en el backend).
  const addResponsableCandidatos = useMemo(
    () => usuariosActivos.filter(u => !ticket?.subtareas.some(s => s.responsable_id === u.id && s.responsable_activo)),
    [usuariosActivos, ticket],
  );
  const addResponsable = useMemo(() => usuariosActivos.find(u => u.id === addResponsableId), [usuariosActivos, addResponsableId]);
  const { data: addTiposData, isFetching: addTiposCargando } = useQuery({
    queryKey: ['tickets-catalogo', addResponsable?.area],
    queryFn: () => get<TicketTipoCatalogo[]>(`/tickets/catalogo/${encodeURIComponent(addResponsable!.area!)}`),
    enabled: addSubtareaAbierto && Boolean(addResponsable?.area),
  });

  function invalidar() {
    queryClient.invalidateQueries({ queryKey: ['tickets'] });
    queryClient.invalidateQueries({ queryKey: ['tickets', 'detalle', id] });
  }

  const cambiarEstatusMutation = useMutation({
    mutationFn: (estatus: TicketEstatus) => patch(`/tickets/${id}/estatus`, { estatus }),
    onSuccess: () => {
      invalidar();
      showToast('success', 'Estatus del ticket actualizado.');
    },
    onError: (err: unknown) => {
      showToast('error', err instanceof ApiError ? err.message : 'No fue posible actualizar el estatus.');
    },
  });

  const devolverMutation = useMutation({
    mutationFn: (comentario: string) => patch(`/tickets/${id}/devolver`, { comentario }),
    onSuccess: () => {
      invalidar();
      showToast('success', 'Ticket marcado como devuelto por información incompleta.');
      setDevolverAbierto(false);
      setDevolverComentario('');
    },
    onError: (err: unknown) => {
      showToast('error', err instanceof ApiError ? err.message : 'No fue posible devolver el ticket.');
    },
  });

  const csatMutation = useMutation({
    mutationFn: () => post(`/tickets/${id}/csat`, { calificacion: csatValor, ...(csatComentario.trim() ? { comentario: csatComentario.trim() } : {}) }),
    onSuccess: () => {
      invalidar();
      showToast('success', '¡Gracias por tu calificación!');
    },
    onError: (err: unknown) => {
      showToast('error', err instanceof ApiError ? err.message : 'No fue posible registrar la calificación.');
    },
  });

  // Secuencial (no Promise.all): así la barra de progreso refleja un solo
  // archivo a la vez en vez de un promedio confuso entre varios en paralelo.
  async function subirArchivos(files: File[]) {
    setSubiendoArchivo(true);
    const fallidos: string[] = [];
    for (const file of files) {
      setProgresoSubida(0);
      try {
        await uploadArchivo(`/tickets/${id}/adjuntos/archivo`, file, setProgresoSubida);
      } catch (err) {
        fallidos.push(`${file.name}${err instanceof ApiError ? ` (${err.message})` : ''}`);
      }
    }
    setSubiendoArchivo(false);
    setProgresoSubida(0);
    invalidar();
    const exitosos = files.length - fallidos.length;
    if (exitosos > 0) showToast('success', `${exitosos} archivo${exitosos !== 1 ? 's' : ''} adjuntado${exitosos !== 1 ? 's' : ''} correctamente.`);
    if (fallidos.length > 0) showToast('error', `No se pudo adjuntar: ${fallidos.join(', ')}`);
  }

  const agregarLinkMutation = useMutation({
    mutationFn: () => post(`/tickets/${id}/adjuntos`, { nombre: linkNombre.trim(), drive_url: linkUrl.trim() }),
    onSuccess: () => {
      invalidar();
      showToast('success', 'Link adjuntado correctamente.');
      setLinkAbierto(false);
      setLinkNombre('');
      setLinkUrl('');
    },
    onError: (err: unknown) => {
      showToast('error', err instanceof ApiError ? err.message : 'No fue posible adjuntar el link.');
    },
  });

  const eliminarAdjuntoMutation = useMutation({
    mutationFn: (adjId: string) => del(`/tickets/${id}/adjuntos/${adjId}`),
    onSuccess: () => {
      invalidar();
      showToast('success', 'Adjunto eliminado.');
    },
    onError: (err: unknown) => {
      showToast('error', err instanceof ApiError ? err.message : 'No fue posible eliminar el adjunto.');
    },
  });

  const actualizarSubtareaMutation = useMutation({
    mutationFn: ({ subtareaId, estado }: { subtareaId: string; estado: 'pendiente' | 'completada' }) =>
      patch(`/tickets/${id}/subtareas/${subtareaId}`, { estado }),
    onSuccess: (_data, vars) => {
      invalidar();
      showToast('success', vars.estado === 'completada' ? 'Sub-tarea marcada como completada.' : 'Sub-tarea marcada como pendiente.');
    },
    onError: (err: unknown) => {
      showToast('error', err instanceof ApiError ? err.message : 'No fue posible actualizar la sub-tarea.');
    },
  });

  const agregarSubtareaMutation = useMutation({
    mutationFn: () => post(`/tickets/${id}/subtareas`, { responsable_id: addResponsableId, tipo_solicitud_id: addTipoId, detalle: addDetalle.trim() }),
    onSuccess: () => {
      invalidar();
      showToast('success', 'Sub-tarea añadida correctamente.');
      setAddSubtareaAbierto(false);
      setAddResponsableId('');
      setAddTipoId('');
      setAddDetalle('');
    },
    onError: (err: unknown) => {
      showToast('error', err instanceof ApiError ? err.message : 'No fue posible añadir la sub-tarea.');
    },
  });

  const proponerCambioMutation = useMutation({
    mutationFn: () => post(`/tickets/${id}/cambio-prioridad`, { prioridad_propuesta: nuevaPrioridad, motivo: motivoPropuesta.trim() }),
    onSuccess: () => {
      invalidar();
      showToast('success', 'Cambio de SLA propuesto — a la espera de aprobación del solicitante.');
      setProponerAbierto(false);
      setNuevaPrioridad('');
      setMotivoPropuesta('');
    },
    onError: (err: unknown) => {
      showToast('error', err instanceof ApiError ? err.message : 'No fue posible proponer el cambio de SLA.');
    },
  });

  const resolverCambioMutation = useMutation({
    mutationFn: (decision: 'aprobado' | 'rechazado') =>
      patch(`/tickets/${id}/cambio-prioridad/${ticket?.propuesta_prioridad_pendiente?.id}`, { decision }),
    onSuccess: (_data, decision) => {
      invalidar();
      showToast('success', decision === 'aprobado' ? 'Cambio de SLA aprobado.' : 'Cambio de SLA rechazado.');
    },
    onError: (err: unknown) => {
      showToast('error', err instanceof ApiError ? err.message : 'No fue posible resolver la propuesta.');
    },
  });

  const editarResponsablesMutation = useMutation({
    mutationFn: (payload: { agregar: string[]; quitar: string[] }) => patch(`/tickets/${id}/responsables`, payload),
    onSuccess: () => {
      invalidar();
      showToast('success', 'Responsables actualizados.');
      setResponsablesAbierto(false);
    },
    onError: (err: unknown) => {
      showToast('error', err instanceof ApiError ? err.message : 'No fue posible actualizar los responsables.');
    },
  });

  const subtareasOrdenadas = useMemo(() => (ticket ? ordenarSubtareas(ticket.folio, ticket.subtareas) : []), [ticket]);
  const subtareasActivas = useMemo(() => ticket?.subtareas.filter(s => s.responsable_activo) ?? [], [ticket]);
  const progresoTotal = subtareasActivas.length;
  const progresoCompletadas = subtareasActivas.filter(s => s.estado === 'completada').length;
  const progresoPct = progresoTotal ? Math.round((progresoCompletadas / progresoTotal) * 100) : 0;

  // Prompt AO (bug #1) — reloj en vivo de horas transcurridas (no existía
  // ninguno hasta este prompt). Se congela en subtareas_completadas_en en
  // vez de seguir avanzando con Date.now() cuando el ticket ya alcanzó el
  // 100% de sub-tareas activas pero sigue sin cerrarse.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 60_000);
    return () => clearInterval(interval);
  }, []);
  const tiempoTranscurridoHrs = useMemo(() => {
    if (!ticket || ticket.fecha_cierre) return null;
    const instanteFin = ticket.subtareas_completadas_en ? new Date(ticket.subtareas_completadas_en) : new Date();
    return (instanteFin.getTime() - new Date(ticket.fecha_creacion).getTime()) / 3_600_000;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket, tick]);

  function abrirEditorResponsables() {
    if (!ticket) return;
    setResponsablesSeleccion(ticket.responsables.map(r => r.id));
    setResponsablesAbierto(true);
  }

  function guardarResponsables() {
    if (!ticket) return;
    const actuales = ticket.responsables.map(r => r.id);
    const agregar = responsablesSeleccion.filter(idSel => !actuales.includes(idSel));
    const quitar = actuales.filter(idAct => !responsablesSeleccion.includes(idAct));
    if (!agregar.length && !quitar.length) {
      setResponsablesAbierto(false);
      return;
    }
    editarResponsablesMutation.mutate({ agregar, quitar });
  }

  function handleArchivosSeleccionados(files: File[]) {
    const validos = files.filter(f => {
      if (f.size > MAX_ADJUNTO_BYTES) {
        showToast('error', `"${f.name}" excede el límite de ${MAX_ADJUNTO_BYTES / (1024 * 1024)}MB.`);
        return false;
      }
      return true;
    });
    if (validos.length) void subirArchivos(validos);
  }

  async function handleDescargar(adjunto: TicketAdjunto) {
    try {
      await descargarArchivo(`/tickets/${id}/adjuntos/${adjunto.id}/descargar`, adjunto.nombre_original ?? adjunto.nombre);
    } catch (err) {
      showToast('error', err instanceof ApiError ? err.message : 'No fue posible descargar el archivo.');
    }
  }

  function confirmarCambioEstatus() {
    if (estatusPendiente) cambiarEstatusMutation.mutate(estatusPendiente);
    setEstatusPendiente(null);
  }

  function confirmarDevolver() {
    if (!devolverComentario.trim()) return;
    devolverMutation.mutate(devolverComentario.trim());
  }

  function confirmarCambioSubtarea() {
    if (subtareaConfirmPendiente) {
      actualizarSubtareaMutation.mutate({ subtareaId: subtareaConfirmPendiente.id, estado: subtareaConfirmPendiente.nuevoEstado });
    }
    setSubtareaConfirmPendiente(null);
  }

  function confirmarResolverCambio() {
    if (decisionPendiente) resolverCambioMutation.mutate(decisionPendiente);
    setDecisionPendiente(null);
  }

  if (isLoading || !ticket) {
    return (
      <div>
        <Link to="/tickets" className="back-btn">
          ← Volver a tickets
        </Link>
        <p style={{ color: 'var(--muted)' }}>Cargando…</p>
      </div>
    );
  }

  // Permisos por-actor (root siempre puede, patrón establecido en el resto del
  // sistema). Multiticket: "el responsable" se generaliza a "cualquiera en
  // ticket.responsables" — Tomar/Retomar/Devolver cualquiera de ellos; Marcar
  // resuelta solo el solicitante; Reabrir el solicitante o cualquier
  // responsable. El backend hace cumplir lo mismo (403) — esto solo evita
  // ofrecer botones condenados a fallar.
  const esRoot = user?.rol === 'root';
  const esResponsable = Boolean(user?.sub) && ticket.responsables.some(r => r.id === user?.sub);
  const esSolicitante = Boolean(user?.sub) && user?.sub === ticket.solicitante_id;

  const puedeTomarORetomar = esRoot || esResponsable;
  const puedeMarcarResuelta = esRoot || esSolicitante;
  const puedeReabrirPermiso = esRoot || esSolicitante || esResponsable;
  const puedeDevolverPermiso = esRoot || esResponsable;
  // Mismo criterio del backend (gestionarResponsables en tickets.service.ts,
  // Prompt Z): solicitante, responsable actual o root — ya no un rol fijo.
  // Reutilizado tal cual para adjuntos y para "Editar responsables": ambos
  // exigen exactamente "ser parte del ticket", no un rol global.
  const puedeGestionarTicket = esRoot || esResponsable || esSolicitante;
  const puedeCancelar = user?.rol && ['root', 'head', 'analista'].includes(user.rol);
  const puedeCalificar =
    ticket.estatus === 'resuelta' &&
    !ticket.csat_respondido &&
    user?.sub === ticket.solicitante_id &&
    !ticket.responsables.some(r => r.id === ticket.solicitante_id);

  const tomadoPor = quienTomo(ticket);

  const confirmInfo = estatusPendiente ? ESTATUS_CONFIRM[estatusPendiente] : null;

  return (
    <div>
      <Link to="/tickets" className="back-btn">
        ← Volver a tickets
      </Link>

      <div className="tk-detail-header">
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div className="tk-detail-folio">{ticket.folio}</div>
            <div className="tk-detail-title">{ticket.titulo}</div>
            <div className="tk-detail-meta">
              <span className="tk-meta-chip">
                <strong>Solicitante:</strong> {ticket.solicitante_nombre} ({ticket.area_solicitante})
              </span>
              {ticket.nivel_servicio_es_externo &&
                ticket.clientes.map(c => (
                  <span className="tk-meta-chip" key={c.id}>
                    <strong>Cliente:</strong> {c.nombre_comercial}
                  </span>
                ))}
              <span className="tk-meta-chip">
                <strong>Tipos:</strong> {ticket.tipos_involucrados.length ? ticket.tipos_involucrados.map(t => t.tipo).join(', ') : '—'}
              </span>
              {/* Prompt AM — solo visual: áreas de las sub-tareas ACTIVAS,
              calculado al vuelo (obtenerAreasInvolucradas). NO es
              ticket.area_responsable (ese campo sigue siendo solo insumo del
              dashboard, derivado del primer responsable — nunca se muestra
              aquí). */}
              <span className="tk-meta-chip">
                <strong>Áreas involucradas:</strong> {ticket.areas_involucradas || '—'}
              </span>
              {tomadoPor && (
                <span className="tk-meta-chip" style={{ background: '#eff6ff', borderColor: '#bfdbfe', color: 'var(--brand)' }}>
                  Tomado por {tomadoPor}
                </span>
              )}
              {ticket.retrabajo && (
                <span className="tk-meta-chip" style={{ background: '#fef2f2', borderColor: '#fecaca', color: 'var(--danger)' }}>
                  <strong>⚠ Retrabajo</strong>
                </span>
              )}
              {ticket.devuelta && (
                <span className="tk-meta-chip" style={{ background: '#fff7ed', borderColor: '#fed7aa', color: 'var(--warning)' }}>
                  <strong>Devuelta por info incompleta</strong>
                </span>
              )}
              {puedeGestionarTicket && (
                <button
                  type="button"
                  className="tk-meta-chip"
                  style={{ cursor: 'pointer', color: 'var(--brand)', background: 'none' }}
                  onClick={abrirEditorResponsables}
                >
                  ✎ Editar responsables
                </button>
              )}
            </div>
            <div style={{ fontSize: 13.5, color: 'var(--body)', lineHeight: 1.6, background: '#f8fafc', border: '1px solid var(--border)', borderRadius: 9, padding: '12px 15px', marginTop: 4 }}>
              {ticket.descripcion}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end', flexShrink: 0 }}>
            <EstatusBadge estatus={ticket.estatus} />
            <PrioridadBadge prioridad={ticket.prioridad} />
          </div>
        </div>

        <div className="tk-actions-bar">
          {ticket.estatus === 'nueva' && puedeTomarORetomar && (
            <Button size="sm" onClick={() => setEstatusPendiente('en_progreso')}>
              Tomar ticket
            </Button>
          )}
          {ticket.estatus === 'en_progreso' && puedeMarcarResuelta && (
            <Button size="sm" onClick={() => setEstatusPendiente('resuelta')}>
              Marcar resuelta
            </Button>
          )}
          {ticket.estatus === 'en_progreso' && puedeCancelar && (
            <Button size="sm" variant="secondary" onClick={() => setEstatusPendiente('cancelada')}>
              Cancelar
            </Button>
          )}
          {ticket.estatus === 'reabierta' && puedeTomarORetomar && (
            <Button size="sm" onClick={() => setEstatusPendiente('en_progreso')}>
              Retomar
            </Button>
          )}
          {puedeReabrirCliente(ticket) && puedeReabrirPermiso && (
            <Button size="sm" variant="secondary" onClick={() => setEstatusPendiente('reabierta')}>
              Reabrir
            </Button>
          )}
          {ticket.estatus !== 'cancelada' && puedeDevolverCliente(ticket) && puedeDevolverPermiso && (
            <Button size="sm" variant="secondary" onClick={() => setDevolverAbierto(true)}>
              Devolver por info incompleta
            </Button>
          )}
        </div>
      </div>

      {/* Prompt AN — bloque VISIBLE (no solo la campanita) mientras haya una
      propuesta de cambio de SLA pendiente. El solicitante (o root) ve
      botones de acción; cualquier otro solo ve el estado informativo. */}
      {ticket.propuesta_prioridad_pendiente && (
        <div
          style={{
            background: '#fffbeb',
            border: '1px solid #fde68a',
            borderRadius: 12,
            padding: 16,
            marginTop: 18,
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--heading)', marginBottom: 6 }}>
            Cambio de SLA propuesto
          </div>
          <div style={{ fontSize: 13, color: 'var(--body)', marginBottom: 4 }}>
            <strong>{ticket.propuesta_prioridad_pendiente.propuesto_por_nombre}</strong> propuso cambiar la prioridad de{' '}
            <strong>{PRIORIDAD_LABEL[ticket.propuesta_prioridad_pendiente.prioridad_actual]}</strong> a{' '}
            <strong>{PRIORIDAD_LABEL[ticket.propuesta_prioridad_pendiente.prioridad_propuesta]}</strong>.
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--muted)', marginBottom: esSolicitante || esRoot ? 12 : 0 }}>
            Motivo: {ticket.propuesta_prioridad_pendiente.motivo}
          </div>
          {esSolicitante || esRoot ? (
            <div style={{ display: 'flex', gap: 8 }}>
              <Button size="sm" onClick={() => setDecisionPendiente('aprobado')} disabled={resolverCambioMutation.isPending}>
                Aprobar
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setDecisionPendiente('rechazado')} disabled={resolverCambioMutation.isPending}>
                Rechazar
              </Button>
            </div>
          ) : (
            <div style={{ fontSize: 12, color: 'var(--muted)', fontStyle: 'italic' }}>
              Esperando aprobación del solicitante.
            </div>
          )}
        </div>
      )}

      {/* Prompt AL — Responsables y Subtareas: una fila por responsable con su
      propio tipo/detalle/estatus. La barra de progreso solo cuenta
      sub-tareas con responsable_activo=true (las inactivas ya no forman
      parte del total). */}
      <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 12, padding: 18, marginTop: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--heading)', textTransform: 'uppercase', letterSpacing: '.5px' }}>
            Responsables y Subtareas
          </div>
          {puedeGestionarTicket && (
            <Button size="sm" variant="secondary" onClick={() => setAddSubtareaAbierto(true)}>
              + Añadir Subtarea
            </Button>
          )}
        </div>

        {progresoTotal > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, color: 'var(--muted)', marginBottom: 4 }}>
              <span>Progreso</span>
              <span>{progresoCompletadas} de {progresoTotal} completadas ({progresoPct}%)</span>
            </div>
            <div style={{ height: 8, borderRadius: 999, background: '#e2e8f0', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${progresoPct}%`, background: 'var(--brand)', borderRadius: 999, transition: 'width .2s' }} />
            </div>
          </div>
        )}

        <div className="table-scroll">
          <table className="main-table">
            <thead>
              <tr>
                <th>Responsable</th>
                <th>Tipo de solicitud</th>
                <th>Detalle</th>
                <th style={{ textAlign: 'center' }}>Estatus</th>
              </tr>
            </thead>
            <tbody>
              {subtareasOrdenadas.length === 0 ? (
                <tr>
                  <td className="empty-td" colSpan={4}>Sin sub-tareas</td>
                </tr>
              ) : (
                subtareasOrdenadas.map(({ subtarea: s, subFolio }) => {
                  const atenuada = !s.responsable_activo || s.estado === 'completada';
                  const puedeCompletar = esRoot || user?.sub === s.responsable_id;
                  // Prompt AO (bug #3) — el ticket debe tomarse antes de
                  // poder marcar sub-tareas (mismo criterio que el backend,
                  // que lo hace cumplir con 409 TICKET_NO_TOMADO).
                  const ticketTomado = ticket.estatus !== 'nueva';
                  return (
                    <Fragment key={s.id}>
                      <tr style={atenuada ? { opacity: 0.55 } : undefined}>
                        <td>{s.responsable_nombre}{!s.responsable_activo && <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--muted)' }}>(quitado)</span>}</td>
                        <td>{s.tipo_solicitud}</td>
                        <td style={{ maxWidth: 320 }}>{s.detalle}</td>
                        <td style={{ textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={s.estado === 'completada'}
                            disabled={!puedeCompletar || !ticketTomado || actualizarSubtareaMutation.isPending}
                            title={!ticketTomado ? 'Toma el ticket para poder marcar tareas' : undefined}
                            onChange={e => setSubtareaConfirmPendiente({ id: s.id, nuevoEstado: e.target.checked ? 'completada' : 'pendiente' })}
                          />
                        </td>
                      </tr>
                      <tr style={{ opacity: 0.65, fontSize: 11.5 }}>
                        <td colSpan={4} style={{ paddingTop: 0, color: 'var(--muted)' }}>
                          {subFolio ?? '—'} · Asignado: {fmtFechaHora(s.asignado_en)}
                          {s.completado_en ? ` · Completado: ${fmtFechaHora(s.completado_en)}` : ''}
                        </td>
                      </tr>
                    </Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 18, alignItems: 'start', marginTop: 18 }}>
        {/* Timeline */}
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--heading)', marginBottom: 14, textTransform: 'uppercase', letterSpacing: '.5px' }}>
            Historial
          </div>
          <div className="tk-timeline">
            {ticket.eventos.map(ev => (
              <div className="tk-event" key={ev.id}>
                <div className="tk-event-icon-wrap">
                  <div className="tk-event-icon">{eventoIcono(ev)}</div>
                  <div className="tk-event-line" />
                </div>
                <div className="tk-event-body">
                  <div className="tk-event-header">
                    <span className="tk-event-actor">{ev.actor_nombre}</span>
                    <span className="tk-event-action">{eventoAccion(ev)}</span>
                    <span className="tk-event-time">{fmtFechaHora(ev.created_at)}</span>
                  </div>
                  {ev.tipo === 'devolucion' && ev.descripcion && <div className="tk-event-note">{ev.descripcion}</div>}
                </div>
              </div>
            ))}
          </div>

          {puedeCalificar && (
            <div className="csat-section">
              <div className="csat-section-title">¿Cómo calificarías la atención recibida?</div>
              <div className="csat-section-sub">Tu calificación ayuda a mejorar el servicio del equipo</div>
              <div className="csat-stars">
                {[1, 2, 3, 4, 5].map(v => (
                  <span
                    key={v}
                    className={`csat-star${v <= (csatHover || csatValor) ? ' on' : ''}`}
                    onClick={() => setCsatValor(v)}
                    onMouseEnter={() => setCsatHover(v)}
                    onMouseLeave={() => setCsatHover(0)}
                  >
                    ★
                  </span>
                ))}
              </div>
              {csatValor > 0 && (
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6 }}>
                  {csatValor} — {CSAT_LABEL[csatValor]}
                </div>
              )}
              <textarea
                rows={2}
                placeholder="Comentario opcional…"
                value={csatComentario}
                onChange={e => setCsatComentario(e.target.value)}
                style={{ width: '100%', marginTop: 10, padding: '8px 10px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 13, resize: 'none' }}
              />
              <Button
                size="sm"
                style={{ marginTop: 10 }}
                disabled={!csatValor || csatMutation.isPending}
                onClick={() => csatMutation.mutate()}
              >
                Enviar calificación
              </Button>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 12, padding: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 14 }}>Detalles</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[
                ['Creado', fmtFechaHora(ticket.fecha_creacion)],
                ['Primera respuesta', fmtFechaHora(ticket.fecha_primera_respuesta)],
                ['Cierre', fmtFechaHora(ticket.fecha_cierre)],
                ['Reabierta', fmtFechaHora(ticket.fecha_reabierta)],
                ['Mes', ticket.mes],
              ].map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12.5 }}>
                  <span style={{ color: 'var(--muted)' }}>{k}</span>
                  <span style={{ color: 'var(--heading)', fontWeight: 500 }}>{v}</span>
                </div>
              ))}
            </div>
          </div>

          <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 12, padding: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 14 }}>Métricas SLA</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                ['SLA Resp. (límite)', `${ticket.resp_hrs_sla ?? '—'}h`],
                ['TPR real', hrsLabel(ticket.tpr_hrs)],
                ['Cumple SLA Resp.', slaIcon(ticket.cumple_sla_resp)],
                ['SLA Reso. (límite)', `${ticket.reso_hrs_sla ?? '—'}h`],
                ['TTR real', hrsLabel(ticket.ttr_hrs)],
                ['Cumple SLA Reso.', slaIcon(ticket.cumple_sla_reso)],
              ].map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12.5 }}>
                  <span style={{ color: 'var(--muted)' }}>{k}</span>
                  <span>{v}</span>
                </div>
              ))}
              {/* Prompt AO (bug #1) — congela en subtareas_completadas_en en
              vez de seguir corriendo con Date.now() una vez el ticket llegó
              al 100% de sub-tareas activas pero sigue sin cerrarse. */}
              {tiempoTranscurridoHrs !== null && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12.5 }}>
                  <span style={{ color: 'var(--muted)' }}>Tiempo transcurrido</span>
                  <span>{tiempoTranscurridoHrs.toFixed(1)} hrs</span>
                </div>
              )}
              {ticket.subtareas_completadas_en && !ticket.fecha_cierre && (
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: 'var(--warning)',
                    background: '#fff7ed',
                    border: '1px solid #fed7aa',
                    borderRadius: 6,
                    padding: '4px 8px',
                    textAlign: 'center',
                  }}
                >
                  Conteo detenido — pendiente de cierre
                </div>
              )}
            </div>
            {/* Prompt AN — "Cambiar el SLA" = proponer un cambio de
            prioridad. Oculto si ya hay una propuesta pendiente (evita el 409
            predecible, mismo criterio que otros botones de este archivo). */}
            {(esRoot || esResponsable) && !ticket.propuesta_prioridad_pendiente && (
              <Button
                size="sm"
                variant="secondary"
                style={{ marginTop: 14, width: '100%' }}
                onClick={() => setProponerAbierto(true)}
              >
                Proponer cambio de SLA
              </Button>
            )}
          </div>

          {ticket.csat !== null && (
            <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 12, padding: 18 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 10 }}>CSAT</div>
              <div className="csat-stars" style={{ marginTop: 0 }}>
                {[1, 2, 3, 4, 5].map(v => (
                  <span key={v} className={`csat-star${v <= ticket.csat! ? ' on' : ''}`} style={{ cursor: 'default' }}>
                    ★
                  </span>
                ))}
                <span style={{ fontSize: 12, color: 'var(--muted)', marginLeft: 4, alignSelf: 'center' }}>{ticket.csat}/5</span>
              </div>
              {ticket.csat_comentario && (
                <div style={{ marginTop: 8, fontSize: 12.5, color: 'var(--body)', background: '#f8fafc', border: '1px solid var(--border)', borderRadius: 7, padding: '8px 10px' }}>
                  {ticket.csat_comentario}
                </div>
              )}
            </div>
          )}

          <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 12, padding: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 10 }}>Adjuntos</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
              {ticket.adjuntos.length === 0 ? (
                <span style={{ fontSize: 12, color: 'var(--muted)' }}>Sin adjuntos</span>
              ) : (
                ticket.adjuntos.map(a => (
                  <div
                    key={a.id}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', background: '#f8fafc', border: '1px solid var(--border)', borderRadius: 7 }}
                  >
                    <span style={{ fontSize: 14 }}>{iconoAdjunto(a)}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {a.tipo === 'url' ? (
                        <a
                          href={a.drive_url ?? undefined}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ fontSize: 12.5, color: 'var(--brand)', textDecoration: 'none', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                          {a.nombre}
                        </a>
                      ) : (
                        <>
                          <div style={{ fontSize: 12.5, color: 'var(--heading)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {a.nombre_original ?? a.nombre}
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--muted)' }}>{fmtTamano(a.tamano_bytes)}</div>
                        </>
                      )}
                    </div>
                    {a.tipo === 'archivo' && (
                      <button
                        type="button"
                        className="icon-btn"
                        aria-label="Descargar adjunto"
                        title="Descargar"
                        onClick={() => handleDescargar(a)}
                      >
                        ⬇
                      </button>
                    )}
                    {puedeGestionarTicket && (
                      <button
                        type="button"
                        className="trash-btn"
                        aria-label="Eliminar adjunto"
                        title="Eliminar"
                        disabled={eliminarAdjuntoMutation.isPending}
                        onClick={() => eliminarAdjuntoMutation.mutate(a.id)}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>

            {puedeGestionarTicket && (
              <>
                <DropzoneArchivo
                  onFilesSelected={handleArchivosSeleccionados}
                  disabled={subiendoArchivo}
                  subiendo={subiendoArchivo}
                  progreso={progresoSubida}
                />
                <Button variant="secondary" size="sm" style={{ marginTop: 10, width: '100%' }} onClick={() => setLinkAbierto(true)}>
                  Adjuntar solo un link
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      <ConfirmModal
        open={estatusPendiente !== null}
        title={confirmInfo?.titulo ?? ''}
        body={confirmInfo?.body ?? ''}
        confirmLabel={confirmInfo?.confirmLabel ?? 'Confirmar'}
        danger={confirmInfo?.danger ?? false}
        onConfirm={confirmarCambioEstatus}
        onCancel={() => setEstatusPendiente(null)}
      />

      <ConfirmModal
        open={subtareaConfirmPendiente !== null}
        title={subtareaConfirmPendiente?.nuevoEstado === 'completada' ? '¿Desea marcar como completada la subtarea?' : '¿Desea marcar la subtarea como pendiente nuevamente?'}
        body="Se actualizará el estatus de esta sub-tarea."
        confirmLabel="Confirmar"
        onConfirm={confirmarCambioSubtarea}
        onCancel={() => setSubtareaConfirmPendiente(null)}
      />

      <Modal
        open={devolverAbierto}
        title="Devolver por información incompleta"
        onClose={() => setDevolverAbierto(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDevolverAbierto(false)}>
              Cancelar
            </Button>
            <Button
              onClick={confirmarDevolver}
              disabled={!devolverComentario.trim() || devolverMutation.isPending}
            >
              {devolverMutation.isPending ? 'Enviando…' : 'Sí, devolver'}
            </Button>
          </>
        }
      >
        <div className="ff full">
          <label>Elementos faltantes de la solicitud *</label>
          <textarea
            rows={3}
            placeholder="Por favor indica los elementos faltantes de la solicitud…"
            value={devolverComentario}
            onChange={e => setDevolverComentario(e.target.value)}
          />
        </div>
      </Modal>

      <Modal
        open={linkAbierto}
        title="Adjuntar solo un link"
        onClose={() => setLinkAbierto(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setLinkAbierto(false)}>
              Cancelar
            </Button>
            <Button
              onClick={() => agregarLinkMutation.mutate()}
              disabled={!linkNombre.trim() || !linkUrl.trim() || agregarLinkMutation.isPending}
            >
              {agregarLinkMutation.isPending ? 'Guardando…' : 'Adjuntar link'}
            </Button>
          </>
        }
      >
        <div className="ff">
          <label>Nombre *</label>
          <input type="text" value={linkNombre} onChange={e => setLinkNombre(e.target.value)} placeholder="Ej. Carpeta de Drive del cliente" />
        </div>
        <div className="ff" style={{ marginTop: 10 }}>
          <label>URL *</label>
          <input type="text" value={linkUrl} onChange={e => setLinkUrl(e.target.value)} placeholder="https://…" />
        </div>
      </Modal>

      <Modal
        open={responsablesAbierto}
        title="Editar responsables"
        onClose={() => setResponsablesAbierto(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setResponsablesAbierto(false)}>
              Cancelar
            </Button>
            <Button onClick={guardarResponsables} disabled={!responsablesSeleccion.length || editarResponsablesMutation.isPending}>
              {editarResponsablesMutation.isPending ? 'Guardando…' : 'Guardar'}
            </Button>
          </>
        }
      >
        <div className="ff">
          <label>Responsables *</label>
          <CheckboxDropdown
            // Prompt BJ-04 — formatUsuarioLabel(): puesto con asterisco si es_interno.
            options={usuariosActivos.map(u => ({ value: u.id, label: u.puesto ? `${u.nombre} (${formatUsuarioLabel(u)})` : u.nombre }))}
            selected={responsablesSeleccion}
            onChange={setResponsablesSeleccion}
            placeholder="Seleccionar responsables..."
          />
          {!responsablesSeleccion.length && <span className="ff-hint">Un ticket debe tener al menos un responsable.</span>}
        </div>
      </Modal>

      <ConfirmModal
        open={decisionPendiente !== null}
        title={decisionPendiente === 'aprobado' ? '¿Aprobar el cambio de SLA propuesto?' : '¿Rechazar el cambio de SLA propuesto?'}
        body={
          decisionPendiente === 'aprobado'
            ? 'La prioridad del ticket cambiará y se notificará a los responsables asignados.'
            : 'La propuesta quedará rechazada y la prioridad del ticket no cambiará.'
        }
        confirmLabel={decisionPendiente === 'aprobado' ? 'Aprobar' : 'Rechazar'}
        danger={decisionPendiente === 'rechazado'}
        onConfirm={confirmarResolverCambio}
        onCancel={() => setDecisionPendiente(null)}
      />

      <Modal
        open={proponerAbierto}
        title="Proponer cambio de SLA"
        onClose={() => setProponerAbierto(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setProponerAbierto(false)}>
              Cancelar
            </Button>
            <Button
              onClick={() => proponerCambioMutation.mutate()}
              disabled={!nuevaPrioridad || motivoPropuesta.trim().length < 10 || proponerCambioMutation.isPending}
            >
              {proponerCambioMutation.isPending ? 'Enviando…' : 'Enviar propuesta'}
            </Button>
          </>
        }
      >
        <div className="ff">
          <label>Nueva prioridad *</label>
          <select value={nuevaPrioridad} onChange={e => setNuevaPrioridad(e.target.value as TicketPrioridad)}>
            <option value="">Seleccionar...</option>
            {(['P1', 'P2', 'P3'] as TicketPrioridad[])
              .filter(p => p !== ticket.prioridad)
              .map(p => (
                <option key={p} value={p}>
                  {PRIORIDAD_LABEL[p]}
                </option>
              ))}
          </select>
        </div>
        <div className="ff" style={{ marginTop: 10 }}>
          <label>Motivo *</label>
          <textarea rows={3} placeholder="Explica por qué debería cambiar la prioridad…" value={motivoPropuesta} onChange={e => setMotivoPropuesta(e.target.value)} />
        </div>
      </Modal>

      <Modal
        open={addSubtareaAbierto}
        title="Añadir Subtarea"
        onClose={() => setAddSubtareaAbierto(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setAddSubtareaAbierto(false)}>
              Cancelar
            </Button>
            <Button
              onClick={() => agregarSubtareaMutation.mutate()}
              disabled={!addResponsableId || !addTipoId || addDetalle.trim().length < 10 || agregarSubtareaMutation.isPending}
            >
              {agregarSubtareaMutation.isPending ? 'Guardando…' : 'Añadir'}
            </Button>
          </>
        }
      >
        <div className="ff">
          <label>Responsable *</label>
          <select value={addResponsableId} onChange={e => { setAddResponsableId(e.target.value); setAddTipoId(''); }}>
            <option value="">Seleccionar...</option>
            {addResponsableCandidatos.map(u => (
              <option key={u.id} value={u.id}>
                {/* Prompt BJ-04 — formatUsuarioLabel(): puesto con asterisco si es_interno. */}
                {u.nombre}{u.area ? ` (${u.area})` : ''}{u.puesto ? ` — ${formatUsuarioLabel(u)}` : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="ff" style={{ marginTop: 10 }}>
          <label>Tipo de solicitud *</label>
          <select value={addTipoId} onChange={e => setAddTipoId(e.target.value)} disabled={!addResponsable?.area}>
            <option value="">{addResponsable?.area ? (addTiposCargando ? 'Cargando…' : 'Seleccionar...') : 'Selecciona un responsable primero'}</option>
            {(addTiposData ?? []).map(t => (
              <option key={t.id} value={t.id}>
                {t.tipo}
              </option>
            ))}
          </select>
        </div>
        <div className="ff" style={{ marginTop: 10 }}>
          <label>Detalle *</label>
          <textarea rows={3} placeholder="Detalle de la sub-tarea…" value={addDetalle} onChange={e => setAddDetalle(e.target.value)} />
        </div>
      </Modal>

      <ToastStack toasts={toasts} onDismiss={dismiss} />
    </div>
  );
}
